import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Maintenance from '../pages/Maintenance';
import { useAuth } from '../contexts/AuthContext';

const API = '';

// Pages that should always be accessible during maintenance
const ALLOWED_PATHS = ['/login', '/admin', '/forgot-password', '/reset-password'];

const MaintenanceWrapper = ({ children }) => {
  const { user } = useAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Track path changes
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    
    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);
    
    // Create a MutationObserver to detect URL changes from React Router
    const observer = new MutationObserver(() => {
      if (window.location.pathname !== currentPath) {
        setCurrentPath(window.location.pathname);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      observer.disconnect();
    };
  }, [currentPath]);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await axios.get(`${API}/api/maintenance/status`, { timeout: 3000 });
        
        if (response.data.maintenance) {
          setMaintenanceMode(true);
          setMaintenanceData(response.data);
          
          // Check if current user is admin (they can bypass maintenance)
          if (user) {
            try {
              const userResponse = await axios.get(`${API}/api/auth/me`, { timeout: 3000 });
              if (userResponse.data.role === 'admin') {
                setIsAdmin(true);
              }
            } catch (e) {
              // User not logged in or token invalid
              setIsAdmin(false);
            }
          } else {
            setIsAdmin(false);
          }
        } else {
          setMaintenanceMode(false);
        }
      } catch (_) {
        // If endpoint fails, assume not in maintenance mode (silent — no console.error)
        setMaintenanceMode(false);
      } finally {
        setLoading(false);
      }
    };

    checkMaintenance();
    
    // Check every 30 seconds for maintenance status changes
    const interval = setInterval(checkMaintenance, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Check if current path is in the allowed list
  const isAllowedPath = ALLOWED_PATHS.some(path => currentPath.startsWith(path));

  // Show loading briefly while checking
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // If in maintenance mode but on an allowed path (login, admin, etc.), let them through
  // This allows admins to log in even during maintenance
  if (maintenanceMode && isAllowedPath) {
    return (
      <>
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-900 py-2 px-4 text-center text-sm font-medium">
          ⚠️ Site is in maintenance mode. {isAdmin ? <a href="/admin" className="underline font-bold">Manage Settings</a> : ''}
        </div>
        <div className="pt-10">
          {children}
        </div>
      </>
    );
  }

  // If in maintenance mode and user is not admin, show maintenance page
  if (maintenanceMode && !isAdmin) {
    return (
      <Maintenance 
        message={maintenanceData.message}
        expectedBack={maintenanceData.expected_back}
        showCountdown={maintenanceData.show_countdown}
      />
    );
  }

  // If admin is viewing during maintenance, show a banner
  if (maintenanceMode && isAdmin) {
    return (
      <>
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-900 py-2 px-4 text-center text-sm font-medium">
          ⚠️ Site is in maintenance mode. Only admins can see this. <a href="/admin" className="underline font-bold">Manage Settings</a>
        </div>
        <div className="pt-10">
          {children}
        </div>
      </>
    );
  }

  return children;
};

export default MaintenanceWrapper;
